import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../reducers';

/**
 * Reason codes for blocking API errors. Extensible for future scenarios
 * (e.g. company-level access blocks).
 */
export type ApiErrorReason = 'rate_limited' | 'account_blocked';

interface ApiErrorState {
  /** When non-null the app should show a full-page error instead of content. */
  reason: ApiErrorReason | null;
  /** Optional human-readable message from the server response. */
  message: string | null;
  /** Milliseconds until the error clears (from Retry-After header). Null = manual retry only. */
  retryAfterMs: number | null;
}

const initialState: ApiErrorState = {
  reason: null,
  message: null,
  retryAfterMs: null,
};

const apiErrorSlice = createSlice({
  name: 'apiError',
  initialState,
  reducers: {
    setApiError(
      state,
      action: PayloadAction<{
        reason: ApiErrorReason;
        message?: string;
        retryAfterMs?: number;
      }>
    ) {
      state.reason = action.payload.reason;
      state.message = action.payload.message ?? null;
      state.retryAfterMs = action.payload.retryAfterMs ?? null;
    },
    clearApiError() {
      return initialState;
    },
  },
});

export const { setApiError, clearApiError } = apiErrorSlice.actions;

// Selectors
export const selectApiError = (state: RootState) => state.apiError;
export const selectApiErrorReason = (state: RootState) =>
  state.apiError.reason;

export default apiErrorSlice.reducer;
